import React, { useState } from 'react';
import { Field } from 'react-final-form';
import classNames from 'classnames';
import FieldMultiSelect from '../FieldMultiSelect/FieldMultiSelect';
import { positionsOptions, sportsPositionOptions } from '../../config/configExtras';
import { FormattedMessage } from 'react-intl';
import { isArrayLength } from '../../util/genericHelpers';
import { sportsAndTeamOptions, TalentLevel } from '../../util/enums';

import css from './CustomExtendedDataField.module.css';

const RenderSports = ({ name, form, formId, label, values }) => {
  const selectedSports = values?.pub_sports || [];
  const existingPositions = values?.positions || [];
  const isProfessional = values?.pub_talent_level === TalentLevel.PROFESSIONAL;

  const normalizedPositions = positionsOptions;

  // Group teams by sport
  const groupedTeams = sportsAndTeamOptions.reduce((acc, item) => {
    if (!acc[item.sport]) acc[item.sport] = [];
    acc[item.sport].push(item.team);
    return acc;
  }, {});

  const groupedPositions = normalizedPositions.reduce((acc, option) => {
    acc[option.parentSport] = acc[option.parentSport] || [];
    acc[option.parentSport].push(option);
    return acc;
  }, {});

  const handleSportChange = selectedSports => {
    const updatedPositions = existingPositions.filter(pos =>
      selectedSports.some(sport => pos.parentSport === sport.value.toLowerCase())
    );
    form.change('positions', updatedPositions);
  };

  const handlePositionChange = (sport, selectedPosition) => {
    const normalizedSport = sport.toLowerCase();
    const updatedPositions = existingPositions.filter(item => item.parentSport !== normalizedSport);
    const positionObject = normalizedPositions.find(
      pos => pos.parentSport === normalizedSport && pos.position === selectedPosition
    );

    if (positionObject) {
      updatedPositions.push({
        sport: positionObject.sport,
        position: positionObject.position,
        parentSport: positionObject.parentSport,
      });
    }

    form.change('positions', updatedPositions);
  };

  return (
    <>
      <div className={css.sportsContainer}>
        <div className={classNames(css.customField, css.customSelectField)}>
          <label className={css.fieldLabel}>{label}</label>
          <Field
            component={FieldMultiSelect}
            isMulti={true}
            id={formId ? `${formId}.${name}` : name}
            name={name}
            options={sportsPositionOptions}
            onChange={handleSportChange}
          />
        </div>
      </div>

      <div className={css.sportsContainer}>
        {isArrayLength(selectedSports) && (
          <div className={css.positionsSection}>
            <div
              className={classNames(css.columnHeaders, {
                [css.teamsColumnHeaders]: isProfessional,
              })}
            >
              <div className={css.columnHeader}>
                <FormattedMessage id="RenderSports.sportsLabel" defaultMessage="Sports" />
              </div>
              <div className={css.columnHeader}>
                <FormattedMessage id="RenderSports.positionLabel" defaultMessage="Position" />
              </div>
              {isProfessional && (
                <div className={css.columnHeader}>
                  <FormattedMessage id="RenderSports.teamLabel" defaultMessage="Team" />
                </div>
              )}
            </div>

            <div className={css.sportsGrid}>
              {selectedSports.map((sport, index) => {
                const normalizedSport = sport.value.toLowerCase();
                const savedPosition =
                  existingPositions.find(item => item.parentSport === normalizedSport)?.position ||
                  '';
                const baseSportSlug = sport.value.replace(/^(mens-|womens-)/, '');
                const normalizedLabel = baseSportSlug
                  .split('-')
                  .map(w => w.charAt(0).toUpperCase() + w.slice(1))
                  .join(' ');

                return (
                  <div
                    key={index}
                    className={classNames(css.sportPositionRow, {
                      [css.sportTeamRow]: isProfessional,
                    })}
                  >
                    <div className={css.sportField}>
                      <Field
                        name={`selectedSport-${index}`}
                        component="select"
                        disabled
                        className={classNames(css.disabledField, css.selectField)}
                        initialValue={sport.value}
                      >
                        <option value={sport.value}>{sport.label}</option>
                      </Field>
                    </div>

                    <div className={css.positionField}>
                      <Field name={`position-${sport.value}`}>
                        {({ input }) => (
                          <select
                            {...input}
                            value={savedPosition}
                            onChange={event => {
                              handlePositionChange(sport.value, event.target.value);
                              input.onChange(event);
                            }}
                            className={css.selectField}
                          >
                            <option value="">Select Position</option>
                            {groupedPositions[normalizedSport]?.map((pos, i) => (
                              <option key={i} value={pos.position}>
                                {pos.position}
                              </option>
                            ))}
                          </select>
                        )}
                      </Field>
                    </div>

                    {isProfessional && (
                      <div className={css.teamField}>
                        <Field name={`team-${sport.value}`}>
                          {({ input, meta }) => {
                            const showCustomInput = input.value === 'custom';
                            return (
                              <>
                                <select
                                  {...input}
                                  onChange={e => {
                                    input.onChange(e.target.value);
                                  }}
                                  className={css.selectField}
                                >
                                  <option value="">Select Team</option>
                                  {groupedTeams[normalizedLabel]?.map((team, i) => (
                                    <option key={i} value={team}>
                                      {team}
                                    </option>
                                  ))}
                                  <option value="custom">Custom</option>
                                </select>

                                {showCustomInput && (
                                  <Field name={`customTeam-${sport.value}`}>
                                    {({ input: customInput }) => (
                                      <input
                                        {...customInput}
                                        type="text"
                                        className={css.customTeamInput}
                                        placeholder="Enter custom team name"
                                      />
                                    )}
                                  </Field>
                                )}
                              </>
                            );
                          }}
                        </Field>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default RenderSports;
